import React, { useEffect, useRef, useState } from "react";
import { Form, Row, Container, Col } from "react-bootstrap";
import { Formik } from "formik";
import { FormattedMessage, useIntl } from "react-intl";
import Loading from "../../components/general/Loading";
import { SubTemplate } from "../general/SubTemplate";
import { FaRegCheckCircle, FaRegSave } from "react-icons/fa";
import { handleError, isNotBusinessError } from "../../utils/handleError";
import { FormSideNavBar } from "../general/FormSideNavBar";
import * as formSteps from "../../components/eaAccreditation";
import { getDistricts } from "../../rest/administrativeDivision";
import { listQualificationLevels } from "../../rest/qualificationLevel";
import {
  decideAccreditationValidity,
  deleteRenewalDocument,
  getEntityAccreditation,
  getEntityAccreditationByNif,
  getEntityAccreditationDecision,
  getRenewal,
  saveRenewal,
  submitRenewal,
  uploadRenewalDocument,
} from "../../rest/entity";
import { getEaAccreditationFormSchema } from "../../pages/public/AddEaAccreditation";
import { listEntityDocuments, uploadEntityDocumentIEFP } from "../../rest/document";
import { EaAccreditationFormCustomValidator } from "../../utils/EaAccreditationFormCustomValidator";
import { AlertError } from "../bootstrap/AlertError";
import { useNavigate, useParams } from "react-router-dom";
import { createCustomErrorMessage } from "../../hooks/errorMessage";
import { SuccessToast } from "../general/SuccessToast";
import { isIEFPUser } from "../../authentication/authenticationHelper";
import { DecisionDialog } from "../bootstrap/DecisionDialog";
import { AlertSuccess } from "../bootstrap/AlertSuccess";
import { EaRenewalDocuments } from "./form/EaRenewalDocuments";
import { EaRenewalDates } from "./form/EaRenewalDates";
import { DocumentType } from "../../models/DocumentType";
import * as yup from "yup";
import { StateAndDateBadge } from "../general/stateBadge/StateAndDateBadge";
import { DateUtils } from "../../utils/DateUtils";

export function AddAccreditationRenewal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navErrors, setNavErrors] = useState([]);
  const [formStep, setFormStep] = useState(0);
  const [districList, setDistrictList] = useState();
  const [districtOptions, setDistrictOptions] = useState();
  const [qualificationLevelList, setQualificationLevelList] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [renewalDocuments, setRenewalDocuments] = useState([]);
  const [renewal, setRenewal] = useState(null);
  const [accreditation, setAccreditation] = useState(null);
  const [formikInitialValues, setFormikInitialValues] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [showSubmittedToast, setShowSubmittedToast] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const [lsItem, setLsItem] = useState(null);
  const { entityNIPC } = useParams();

  const intl = useIntl();
  const navigate = useNavigate();
  const isIEFP = isIEFPUser();

  const formikPropsRef = useRef(null);

  const acceptedQualificationLevels = ["4", "6", "7", "8"];
  const managerAcceptedQualificationLevels = ["6", "7", "8"];

  const tabsIds = [
    "eaAccreditationForm.sideNavBar.identificationEA",
    "eaAccreditationForm.sideNavBar.experienceEA",
    "eaAccreditationForm.sideNavBar.geographicalArea",
    "eaAccreditationForm.sideNavBar.resources1",
    "eaAccreditationForm.sideNavBar.resources2",
    "eaAccreditationForm.sideNavBar.resources3",
    "eaAccreditationForm.sideNavBar.declaration",
    "eaAccreditationForm.sideNavBar.renewal.dates",
    "eaAccreditationForm.sideNavBar.renewal.docs",
  ];

  const requiredDocuments = ["AT_DECLARATION", "SS_DECLARATION"];

  const requiredRenewalDocuments = [
    DocumentType.RENEWAL_INVITATION_LETTER,
    DocumentType.RENEWAL_ENTITY_RESPONSE_TO_INVITATION,
    DocumentType.RENEWAL_DIRECTIVE_COUNCIL_SERVICE_INFORMATION,
    DocumentType.RENEWAL_DIRECTIVE_COUNCIL_DELIBERATION,
    DocumentType.RENEWAL_NOTIFICATION_LETTER_AND_PROTOCOL,
    DocumentType.RENEWAL_SIGNED_PROTOCOL,
  ];

  useEffect(() => {
    setLoading(true);
    if (submitted) {
      setSubmitted(() => false);
    } else {
      fetchData();
      checkLocalStorageItems();
    }
  }, [submitted]);

  function checkLocalStorageItems() {
    [
      "eaAccreditationRenewalSubmited",
      "eaAccreditationApproved",
      "eaAccreditationDocsNeeded",
      "eaAccreditationAppealed",
      "eaAccreditationRejected",
      "eaAccreditationFullyApproved",
    ].forEach((s) => {
      if (sessionStorage.getItem(s)) {
        setLsItem(s);
      }
    });
  }

  async function fetchData() {
    try {
      let [
        { data: renewalRes },
        { data: districts },
        { data: qualificationLevels },
        { data: currAccred },
      ] = await Promise.all([
        await getRenewal(entityNIPC === undefined ? null : entityNIPC),
        await getDistricts(true),
        await listQualificationLevels(),
        await (entityNIPC === undefined
          ? getEntityAccreditation()
          : getEntityAccreditationByNif(entityNIPC)),
      ]);

      setRenewal(renewalRes);
      setAccreditation(currAccred);

      const { data: entityDocuments } = await listEntityDocuments(
        entityNIPC === undefined ? null : { entityNif: entityNIPC }
      );
      let entityAccreditationDocuments = requiredDocuments.map((rd) => {
        let dTypeIndex = entityDocuments.findIndex(
          (d) => d.documentType === rd
        );
        if (dTypeIndex < 0) {
          //it means the document wasnt found
          return {
            externalId: "",
            documentType: rd,
            name: "",
            content: null,
            submited: false,
            error: "",
          };
        } else {
          return {
            externalId: entityDocuments[dTypeIndex].externalId,
            documentType: entityDocuments[dTypeIndex].documentType,
            documentPath: entityDocuments[dTypeIndex].documentPath,
            name: entityDocuments[dTypeIndex].name,
            content: "",
            submited: true,
            error: "",
          };
        }
      });
      setDocuments(entityAccreditationDocuments);

      let renDocs =
        renewalRes.documents && Array.isArray(renewalRes.documents)
          ? renewalRes.documents
          : [];
      let renewalDocumentsLoad = requiredRenewalDocuments.map((rd) => {
        let dTypeIndex = renDocs.findIndex((d) => d.documentType === rd);
        if (dTypeIndex < 0) {
          //it means the document wasnt found
          return {
            externalId: "",
            documentType: rd,
            name: "",
            content: null,
            submited: false,
            error: "",
          };
        } else {
          return {
            externalId: renDocs[dTypeIndex].externalId,
            documentType: renDocs[dTypeIndex].documentType,
            documentPath: renDocs[dTypeIndex].documentPath,
            name: renDocs[dTypeIndex].name,
            content: "",
            submited: true,
            error: "",
          };
        }
      });
      setRenewalDocuments(renewalDocumentsLoad);

      setFormikInitialValues(getFormikInitialValues(renewalRes, districts));

      setDistrictList(districts);
      let districtNamesList = [];
      districts.map((districtObj) =>
        districtNamesList.push(districtObj.description)
      );
      setDistrictOptions(districtNamesList);
      setQualificationLevelList(qualificationLevels);
    } catch (error) {
      console.log(error);
      setError(error);
    } finally {
      setLoading(false);
    }
  }

  function scrollPageToTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  function handleNavError(error) {
    let errors = error.response.data.message
      .split(",")
      .map((value) => Number.parseInt(value));
    setNavErrors(errors);
  }

  function handlePreviousStep() {
    clearSessionSuccessMessage();
    scrollPageToTop();
    setFormStep(formStep - 1);
  }

  function handleNextStep() {
    clearSessionSuccessMessage();
    scrollPageToTop();
    setFormStep(formStep + 1);
  }

  function clearSessionSuccessMessage() {
    if (lsItem) {
      sessionStorage.removeItem(lsItem);
      setLsItem(null);
    }
  }

  function handleSetFormStep(nextStep) {
    clearSessionSuccessMessage();
    setFormStep(nextStep);
  }

  function parseBackendInterventionTypeToFrontendKeys(type) {
    switch (type) {
      case "Investment_Business_Plans":
        return "entitiesPerYearInvestment";

      case "FOLLOW_UP":
        return "entitiesPerYearFollowUp";

      case "CONSULTING":
        return "entitiesPerYearConsultancy";

      case "INCUBATOR":
        return "entitiesPerYearIncubation";

      case "DOING_WORKSHOPS":
        return "actionsPerYearWorkshops";

      case "DOING_SEMINARS":
        return "actionsPerYearSeminaries";

      case "DOING_COURSEWARE":
        return "actionsPerYearDidacticMaterials";

      case "DOING_BOOTCAMP":
        return "actionsPerYearBootCamps";

      default:
        console.log("Something's wrong");
    }
  }

  function parseBackendMaterialResourceTypeToFrontendKeys(type) {
    switch (type) {
      case "ATTENDANCE_ROOM":
        return "attendanceRooms";

      case "MEET_ROOM":
        return "meetingRooms";

      case "FORMATION_ROOM":
        return "formationRooms";

      case "INCUBATOR":
        return "incubators";

      case "INCUBATOR_SPACE":
        return "incubationSpaces";

      case "PHONES_NUMBER":
        return "phones";

      case "COMPUTER":
        return "computers";

      case "INTERNET_SPOT":
        return "internetPoints";

      case "WIFI":
        return "wifis";

      case "SCANNER":
        return "scanners";

      case "PHOTOCOPIER":
        return "printers";

      case "VEHICLE":
        return "vehicles";

      default:
        console.log("Something's wrong");
    }
  }

  function getFormikInitialValues(eaRenewal, districts) {
    let interventions = {};
    let eaAccreditation = eaRenewal.accreditation;

    eaAccreditation.interventions.forEach((intervention) => {
      interventions[
        parseBackendInterventionTypeToFrontendKeys(intervention.type)
      ] = {
        ...interventions[
          parseBackendInterventionTypeToFrontendKeys(intervention.type)
        ],
        [intervention.year]: {
          value: intervention.value,
          externalId: intervention.externalId,
        },
      };
    });

    let equipmentDistribution = {};
    eaAccreditation.equipmentDistribution?.forEach((distribution) => {
      let districtName = districts.filter(
        (d) => d.code === distribution.districtCode
      )[0].description;

      equipmentDistribution[districtName] = {
        ...equipmentDistribution[districtName],
        [parseBackendMaterialResourceTypeToFrontendKeys(
          distribution.materialResourceType
        )]: {
          externalId: distribution.externalId,
          value: distribution.value,
        },
      };
    });

    let entityResponsibles = eaAccreditation.entity.responsibles;
    entityResponsibles.forEach((er) => {
      er.name = er.name ? er.name : "";
      er.duty = er.duty ? er.duty : "";
    });

    return {
      entity: {
        description: eaAccreditation.entity.description
          ? eaAccreditation.entity.description
          : "",
        nif: eaAccreditation.entity.nif ? eaAccreditation.entity.nif : "",
        address: eaAccreditation.entity.address
          ? eaAccreditation.entity.address
          : "",
        postalCode4Digits: eaAccreditation.entity.postalCode4Digits
          ? eaAccreditation.entity.postalCode4Digits
          : "",
        postalCode3Digits: eaAccreditation.entity.postalCode3Digits
          ? eaAccreditation.entity.postalCode3Digits
          : "",
        location: eaAccreditation.entity.location
          ? eaAccreditation.entity.location
          : "",
        phone: eaAccreditation.entity.phone ? eaAccreditation.entity.phone : "",
        email: eaAccreditation.entity.email ? eaAccreditation.entity.email : "",
        districtCode: eaAccreditation.entity.districtCode
          ? eaAccreditation.entity.districtCode
          : "-1",
        profile: eaAccreditation.entity.profile
          ? eaAccreditation.entity.profile
          : "-1",
        profileDescription: eaAccreditation.entity.profileDescription
          ? eaAccreditation.entity.profileDescription
          : "",
        cae: eaAccreditation.entity.cae ? eaAccreditation.entity.cae : "",
        score: eaAccreditation.entity.score ? eaAccreditation.entity.score : "",
        state: eaAccreditation.entity.state,
        connectionWithIEFP: eaAccreditation.entity.connectionWithIEFP,
        connectionWithIEFPDesc: eaAccreditation.entity.connectionWithIEFPDesc
          ? eaAccreditation.entity.connectionWithIEFPDesc
          : "",
        consultingAreas: eaAccreditation.entity.consultingAreas
          ? eaAccreditation.entity.consultingAreas
          : [],
        atDeclarationAuthorized: eaAccreditation.entity.atDeclarationAuthorized,
        ssDeclarationAuthorized: eaAccreditation.entity.ssDeclarationAuthorized,
        nationalMemberYear: eaAccreditation.entity.nationalMemberYear
          ? eaAccreditation.entity.nationalMemberYear
          : "",
        responsibles: entityResponsibles,
      },
      ...interventions,
      getList: (eaAccreditation.getList || []).map((g) => ({
        ...g,
        locked: true,
      })),
      interventionDistrictCodes: eaAccreditation.interventionDistrictCodes
        ? eaAccreditation.interventionDistrictCodes
        : [],
      tgpList: (eaAccreditation.tgpList || []).map((tgp) => ({
        ...tgp,
        isNew: false,
        deactivated:
          typeof tgp.deactivated === "boolean"
            ? tgp.deactivated
            : tgp.active === false,
      })),
      incubatorLocations: eaAccreditation.incubatorLocations
        ? eaAccreditation.incubatorLocations
        : [],
      equipmentDistribution: eaAccreditation.equipmentDistribution
        ? equipmentDistribution
        : {},
      startDate: eaRenewal?.startDate,
      endDate: eaRenewal?.endDate,
    };
  }

  if (submitted) {
    //return <Navigate replace to={'/'} />;
  }

  if (loading) {
    return <Loading />;
  }

  const eaAccreditationFormSchema = getEaAccreditationFormSchema().shape({
    startDate: yup
      .date()
      .min(
        DateUtils.nextDay(new Date(accreditation?.startDate || new Date(0))),
        <FormattedMessage id="ea.accreditation.renewal.startDate.bellowMin" />
      )
      .test(
        "not-in-future",
        <FormattedMessage id={"ea.accreditation.renewal.startDate.beforeToday"} />,
        (startDate) => {
          // startDate must be today or before -> startDate < nextDay(today)
          return !startDate || startDate < DateUtils.nextDay(new Date());
        }
      )
      .required(<FormattedMessage id="errors.fieldRequiredText" />),
/*     endDate: yup
      .date()
      .min(
        DateUtils.nextDay(new Date(accreditation?.endDate || new Date(0))),
        <FormattedMessage id="ea.accreditation.renewal.endDate.bellowMin" />
      )
      .required(<FormattedMessage id="errors.fieldRequiredText" />)
      .test(
        "is-after-start",
        <FormattedMessage
          id={"ea.accreditation.renewal.endDate.bellowStartDate"}
        />,
        (endDate, context) => {
          const { startDate } = context.parent;
          return !startDate || !endDate || startDate < endDate;
        }
      ), */
  });

  const isDraft = renewal?.state === "DRAFT";
  const canEditDraft = isIEFP && isDraft;

  const readMode = !canEditDraft;

  const mode = isDraft ? "RENEWAL_DRAFT" : "RENEWAL_LOCKED";

  if (error && isNotBusinessError(error)) {
    return handleError(error);
  }

  function parseInterventionKey(key) {
    switch (key) {
      case "entitiesPerYearInvestment":
        return "Investment_Business_Plans";

      case "entitiesPerYearFollowUp":
        return "FOLLOW_UP";

      case "entitiesPerYearConsultancy":
        return "CONSULTING";

      case "entitiesPerYearIncubation":
        return "INCUBATOR";

      case "actionsPerYearWorkshops":
        return "DOING_WORKSHOPS";

      case "actionsPerYearSeminaries":
        return "DOING_SEMINARS";

      case "actionsPerYearDidacticMaterials":
        return "DOING_COURSEWARE";

      case "actionsPerYearBootCamps":
        return "DOING_BOOTCAMP";
      default:
        console.log("Something's wrong");
    }
  }

  function parseMaterialResourceTypeKey(key) {
    switch (key) {
      case "attendanceRooms":
        return "ATTENDANCE_ROOM";

      case "meetingRooms":
        return "MEET_ROOM";

      case "formationRooms":
        return "FORMATION_ROOM";

      case "incubators":
        return "INCUBATOR";

      case "incubationSpaces":
        return "INCUBATOR_SPACE";

      case "phones":
        return "PHONES_NUMBER";

      case "computers":
        return "COMPUTER";

      case "internetPoints":
        return "INTERNET_SPOT";

      case "wifis":
        return "WIFI";

      case "scanners":
        return "SCANNER";

      case "printers":
        return "PHOTOCOPIER";

      case "vehicles":
        return "VEHICLE";

      default:
        console.log("Something's wrong");
    }
  }

  function parseInterventions(formikValues) {
    let interventions = [];

    Object.keys(formikValues)
      .filter(
        (key) =>
          key.startsWith("entitiesPerYear") || key.startsWith("actionsPerYear")
      )
      .map((interventionKey) =>
        Object.keys(formikValues[interventionKey]).map((year) => {
          interventions.push({
            type: parseInterventionKey(interventionKey),
            year: year,
            value: formikValues[interventionKey][year].value,
            externalId: formikValues[interventionKey][year].externalId,
          });
        })
      );

    return interventions;
  }

  function parseEquipmentDistribution(formikValues) {
    let equipmentDistribution = [];

    Object.keys(formikValues.equipmentDistribution).map((key) =>
      Object.keys(formikValues.equipmentDistribution[key]).map((material) =>
        equipmentDistribution.push({
          districtCode: districList.filter(
            (district) => district.description === key
          )[0].code,
          materialResourceType: parseMaterialResourceTypeKey(material),
          value: formikValues.equipmentDistribution[key][material].value,
          externalId:
            formikValues.equipmentDistribution[key][material].externalId,
        })
      )
    );

    return equipmentDistribution;
  }

  function normalizeGetList(getList = []) {
    const byRD = new Map();

    getList
      .filter((g) => g.active !== false)
      .forEach((g) => {
        const rd = g.regionalDelegationCode;
        const current = byRD.get(rd);

        if (!current || g.isNew) {
          byRD.set(rd, g);
        }
      });

    return Array.from(byRD.values()).map((g) => {
      const out = {
        type: "GET",
        regionalDelegationCode: g.regionalDelegationCode,
        name: (g.name || "").trim(),
        phone: g.phone || "",
        mobile: g.mobile || "",
        email: (g.email || "").trim(),
        managementExperience: !!g.managementExperience,
        qualificationLevelCode: g.qualificationLevelCode,
      };
      if (g.externalId) {
        out.externalId = g.externalId;
      }
      return out;
    });
  }

  function parseFormikValuesToAPICall() {
    let formikValues = formikPropsRef.current.values;
    function formatToDateOnly(value) {
      if (!value && value !== 0) return undefined;
      try {
        // if already a string date-only (YYYY-MM-DD)
        if (typeof value === "string" && value.indexOf("T") < 0 && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }
        // if ISO datetime string
        if (typeof value === "string" && value.indexOf("T") > -1) {
          return value.split("T")[0];
        }
        const d = new Date(value);
        if (isNaN(d.getTime())) return undefined;
        return d.toISOString().split("T")[0];
      } catch (e) {
        return undefined;
      }
    }

    const computedEndDate = (() => {
      if (formikValues.endDate) return formatToDateOnly(formikValues.endDate);
      if (formikValues.startDate) {
        const d = new Date(formikValues.startDate);
        d.setFullYear(d.getFullYear() + 3);
        return formatToDateOnly(d);
      }
      return undefined;
    })();

    const formattedStartDate = formatToDateOnly(formikValues.startDate);

    return {
      entity: formikValues.entity,

      getList: normalizeGetList(formikValues.getList),

      interventions: parseInterventions(formikValues),

      interventionDistrictCodes: formikValues.interventionDistrictCodes,

      tgpList: prepareTgpsForApi(formikValues.tgpList),

      incubatorLocations: formikValues.incubatorLocations,

      equipmentDistribution: parseEquipmentDistribution(formikValues),

      startDate: formattedStartDate,

      endDate: computedEndDate,
    };
  }

  async function handleSave() {
    try {
      let formikParsedValues = parseFormikValuesToAPICall();
      let accreditationRenewalDto = {
        startDate: formikParsedValues.startDate,
        endDate: formikParsedValues.endDate,
        accreditation: formikParsedValues,
      };
      let { data: res } = await saveRenewal(
        entityNIPC,
        accreditationRenewalDto
      );
      formikPropsRef.current.setValues(
        getFormikInitialValues(res, districList)
      );
      setRenewal(res);
      setNavErrors([]);
      formikPropsRef.current.setErrors({});

      setShowSaveToast(true);
    } catch (error) {
      console.log(error);
      setError(error);
    }
  }

  async function validateAndOpenSubmissionModal() {
    let errors = await validateForm();
    setNavErrors(errors);
    if (errors.length === 0) {
      setShowSubmitModal(true);
    }
  }

  async function handleSubmit() {
    let formikParsedValues = parseFormikValuesToAPICall();
    let accreditationRenewalDto = {
      startDate: formikParsedValues.startDate,
      endDate: formikParsedValues.endDate,
      accreditation: formikParsedValues,
    };
    try {
      let { data: res } = await submitRenewal(
        entityNIPC,
        accreditationRenewalDto
      );
      setRenewal(res);
      setFormikInitialValues(getFormikInitialValues(res, districList));
      setSubmitted(true);
      sessionStorage.setItem("eaAccreditationRenewalSubmited", true);
      setShowSubmittedToast(true);
    } catch (err) {
      console.log(err);
      if (err?.response?.data?.exception === "ValidationException") {
        handleNavError(err);
      }
      setError(err);
    }
  }

  async function submitValidate() {
    try {
      await decideAccreditationValidity(entityNIPC, true, null);
      setSubmitted(true);
    } catch (error) {
      console.log(error);
      setError(error);
    }
  }

  const entityFieldLabelKeys = {
    description: "eaAccreditationForm.step1.description",
    nif: "eaAccreditationForm.step1.nif",
    address: "eaAccreditationForm.step1.address",
    postalCode4Digits: "eaAccreditationForm.step1.postalCode",
    postalCode3Digits: "eaAccreditationForm.step1.postalCode",
    location: "eaAccreditationForm.step1.location.placeholder",
    phone: "eaAccreditationForm.step1.phone",
    email: "eaAccreditationForm.step1.email",
    districtCode: "eaAccreditationForm.step1.district",
    profile: "eaAccreditationForm.step1.type",
    profileDescription: "eaAccreditationForm.step1.type",
    cae: "eaAccreditationForm.step1.cae",
    score: "eaAccreditationForm.step1.score",
    nationalMemberYear: "eaAccreditationForm.step1.nationalMemberYear",
  };

  const getFieldLabelKeys = {
    name: "eaAccreditationForm.step1.getList.name",
    phone: "eaAccreditationForm.step1.getList.phone",
    mobile: "eaAccreditationForm.step1.getList.mobile",
    email: "eaAccreditationForm.step1.getList.email",
  };

  async function validateForm() {
    let errors = [];

    const formikErrors = await formikPropsRef.current.validateForm();

    const currentValues = formikPropsRef.current.values || {};
    (currentValues.getList || []).forEach((g, i) => {
      if (
        g.isNew &&
        (!g.qualificationLevelCode || g.qualificationLevelCode === "-1")
      ) {
        formikPropsRef.current.setFieldError(
          `getList[${i}].qualificationLevelCode`,
          intl.formatMessage({ id: "errors.fieldRequiredText" })
        );
        errors.push(3);
      }
    });

    let errorMessages = [];

    Object.keys(formikErrors).forEach((key) => {
      switch (key) {
        case "entity":
          Object.keys(formikErrors[key]).forEach((entityErrorKey) => {
            if (
              entityErrorKey === "connectionWithIEFP" ||
              entityErrorKey === "connectionWithIEFPDesc" ||
              entityErrorKey === "consultingAreas"
            )
              errors.push(1);
            else if (entityErrorKey === "responsibles") {
              errors.push(6);
            } else {
              errors.push(0);
              const labelKey = entityFieldLabelKeys[entityErrorKey];
              const fieldLabel = labelKey
                ? intl.formatMessage({ id: labelKey })
                : entityErrorKey;
              errorMessages.push(
                intl.formatMessage(
                  { id: "errors.eaAccreditationForm.entityField" },
                  { field: fieldLabel }
                )
              );
            }
          });
          break;
        case "getList":
          if (Array.isArray(formikErrors[key])) {
            formikErrors[key].forEach(
              (getErrorObj, idx) =>
                getErrorObj !== undefined &&
                Object.keys(getErrorObj).forEach((getErrorKey) => {
                  if (getErrorKey === "qualificationLevelCode") {
                    errors.push(3);
                  } else {
                    errors.push(0);
                    const labelKey = getFieldLabelKeys[getErrorKey];
                    const fieldLabel = labelKey
                      ? intl.formatMessage({ id: labelKey })
                      : getErrorKey;
                    errorMessages.push(
                      intl.formatMessage(
                        { id: "errors.eaAccreditationForm.getField" },
                        { index: idx + 1, field: fieldLabel }
                      )
                    );
                  }
                })
            );
          } else {
            errors.push(0);
            errorMessages.push(
              intl.formatMessage({ id: "errors.eaAccreditationForm.getListInvalid" })
            );
          }
          break;
        case "entitiesPerYearInvestment":
          errors.push(1);
          break;
        case "entitiesPerYearFollowUp":
          errors.push(1);
          break;
        case "entitiesPerYearConsultancy":
          errors.push(1);
          break;
        case "entitiesPerYearIncubation":
          errors.push(1);
          break;
        case "actionsPerYearWorkshops":
          errors.push(1);
          break;
        case "actionsPerYearSeminaries":
          errors.push(1);
          break;
        case "actionsPerYearDidacticMaterials":
          errors.push(1);
          break;
        case "actionsPerYearBootCamps":
          errors.push(1);
          break;
        case "interventionDistrictCodes":
          errors.push(2);
          break;
        case "tgpList":
          errors.push(3);
          break;
        case "incubatorLocations":
          errors.push(4);
          break;
        case "equipmentDistribution":
          errors.push(5);
          break;
        case "startDate":
        case "endDate":
          errors.push(7);
          break;
        default:
          break;
      }
    });

    if (
      !EaAccreditationFormCustomValidator.validateGETListSize(
        formikPropsRef.current.values,
        setError,
        intl,
        errorMessages
      )
    ) {
      errors.push(0);
    }

    if (
      !EaAccreditationFormCustomValidator.validateTGPListSize(
        formikPropsRef.current.values,
        setError,
        intl,
        errorMessages
      )
    ) {
      errors.push(3);
    }

    if (
      !EaAccreditationFormCustomValidator.validateSsAndAtAuthorizations(
        formikPropsRef.current.values,
        documents,
        setError,
        intl,
        errorMessages
      )
    ) {
      errors.push(6);
    }

    if (
      !EaAccreditationFormCustomValidator.validateGETAndDistricts(
        formikPropsRef.current.values,
        setError,
        intl,
        errorMessages,
        formikPropsRef.current.setFieldError,
        districList,
        true
      )
    ) {
      errors.push(0);
      errors.push(2);
    }

    if (
      !EaAccreditationFormCustomValidator.validateRenewalDocuments(
        formikPropsRef.current.values,
        renewalDocuments,
        setError,
        intl,
        errorMessages
      )
    ) {
      errors.push(8);
    }

    if (
      !EaAccreditationFormCustomValidator.validateNewTgps(
        formikPropsRef.current.values,
        intl,
        formikPropsRef.current.setFieldError,
        errorMessages
      )
    ) {
      errors.push(3);
    }

    if (
      !EaAccreditationFormCustomValidator.validateTgpMappings(
        formikPropsRef.current.values,
        intl,
        errorMessages
      )
    ) {
      errors.push(3);
    }

    if (errorMessages.length > 0) {
      let message =
        errorMessages.length === 1
          ? createCustomErrorMessage(errorMessages[0])
          : createCustomErrorMessage(
              "<p>" +
                intl.formatMessage({
                  id: "errors.more",
                }) +
                "</p><p> - " +
                errorMessages.join("</p><p> - ") +
                "</p>"
            );

      setError(message);
    } else {
      setError(null);
    }

    return errors;
  }

  function renderStep(formikProps) {
    formikPropsRef.current = formikProps;

    switch (formStep) {
      case 0:
        return (
          <formSteps.EaAccreditationFormStep1
            formStep={1}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handleNextStep={handleNextStep}
            setFieldValue={formikProps.setFieldValue}
            districts={districList}
            districtOptions={districtOptions}
            readMode={readMode}
            lockNonGet={true}
            allowGetEdit={false}
            syncGetWithIntervention={false}
          />
        );
      case 1:
        return (
          <formSteps.EaAccreditationFormStep2
            formStep={2}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setFieldValue={formikProps.setFieldValue}
            tableHeaders={Object.keys(
              formikProps.values.entitiesPerYearInvestment
            )}
            readMode={true}
          />
        );
      case 2:
        return (
          <formSteps.EaAccreditationFormStep3
            formStep={3}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            districts={districList}
            setFieldValue={formikProps.setFieldValue}
            readMode={true}
          />
        );
      case 3:
        return (
          <formSteps.EaAccreditationFormStep4
            formStep={4}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            handleChange={formikProps.handleChange}
            setFieldValue={formikProps.setFieldValue}
            qualificationLevelOptions={qualificationLevelList
              .filter((ql) => acceptedQualificationLevels.includes(ql.code))
              .map((ql) => ql.code)}
            managerQualificationLevelOptions={qualificationLevelList
              .filter((ql) =>
                managerAcceptedQualificationLevels.includes(ql.code)
              )
              .map((ql) => ql.code)}
            districts={districList}
            renewalStartDate={formikProps.values.startDate}
            renewalEndDate={formikProps.values.endDate}
            readMode={readMode}
          />
        );
      case 4:
        return (
          <formSteps.EaAccreditationFormStep5
            formStep={5}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setFieldValue={formikProps.setFieldValue}
            districts={districList.filter((d) =>
              formikProps.values.interventionDistrictCodes.includes(d.code)
            )}
            setError={setError}
            readMode={true}
          />
        );
      case 5:
        return (
          <formSteps.EaAccreditationFormStep6
            formStep={6}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setFieldValue={formikProps.setFieldValue}
            districts={districList.filter((d) =>
              formikProps.values.interventionDistrictCodes.includes(d.code)
            )}
            districtOptions={districtOptions}
            readMode={true}
          />
        );
      case 6:
        return (
          <formSteps.EaAccreditationFormStep7
            formStep={7}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setError={setError}
            documents={documents}
            setDocuments={setDocuments}
            uploadFunction={(formData) =>
              uploadEntityDocumentIEFP(formData, entityNIPC)
            }
            mode={mode}
          />
        );
      case 7:
        return (
          <EaRenewalDates
            formStep={8}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            handleNextStep={handleNextStep}
            setFieldValue={formikProps.setFieldValue}
            readMode={readMode}
            hideEndDate={true}
          />
        );
      case 8:
        return (
          <EaRenewalDocuments
            formStep={9}
            numberOfSteps={tabsIds.length}
            errors={formikProps.errors}
            formikValues={formikProps.values}
            handleChange={formikProps.handleChange}
            handlePreviousStep={handlePreviousStep}
            setError={setError}
            documents={renewalDocuments}
            setDocuments={setRenewalDocuments}
            uploadFunction={(formData) =>
              uploadRenewalDocument(entityNIPC, formData)
            }
            deleteFunction={(documentExternalId) =>
              deleteRenewalDocument(entityNIPC, documentExternalId)
            }
            readMode={readMode}
          />
        );
      default:
        console.log("Something went wrong rendering the form step");
    }
  }

  function prepareTgpsForApi(list = []) {
    return list.map((t) => {
      const {
        externalId,
        name,
        qualificationLevelCode,
        hre = {},
        hrMapList = [],
        active,
        deactivatedAt,
      } = t;

      const out = {
        name: (name || "").trim(),
        qualificationLevelCode,
        hre: {
          nif: hre.nif || "",
          bondType: hre.bondType || "",
          projectCreationCount: hre.projectCreationCount ?? 0,
          approvedProjectFollowUpCount: hre.approvedProjectFollowUpCount ?? 0,
          consultingCount: hre.consultingCount ?? 0,
          expandSkillsCount: hre.expandSkillsCount ?? 0,
          analysisCount: hre.analysisCount ?? 0,
        },
        hrMapList,
        active: active === false ? false : true,
      };

      if (externalId) out.externalId = externalId;
      if (deactivatedAt) out.deactivatedAt = deactivatedAt;

      return out;
    });
  }

  return (
    <SubTemplate
      titleId={"ea.accreditation.renewal.title"}
      subTitleId={"eaAccreditationForm.subTitle"}
      hasBackButton={true}
      centerContent={true}
      stateBadgeProps={StateAndDateBadge.propsFromAccreditationRenewal(renewal)}
    >
      <Container>
        <DecisionDialog
          dialogClassName="modal-35"
          show={showSubmitModal}
          handleConfirm={() => {
            setShowSubmitModal(false);
            handleSubmit();
          }}
          handleClose={() => {
            setShowSubmitModal(false);
          }}
          titleId={"ea.accreditation.renewal.submitModal.title"}
          bodyId={"ea.accreditation.renewal.submitModal.body"}
          needReason={false}
        />
        <Row className="mb-5">
          <Col md="2">
            <FormSideNavBar
              tabsIdsArray={tabsIds}
              formStep={formStep}
              setFormStep={handleSetFormStep}
              navErrors={navErrors}
              setNavErrors={setNavErrors}
            />
          </Col>
          <Col md="8">
            <AlertError error={error} />
            {lsItem && <AlertSuccess lsItem={lsItem} />}
            <SuccessToast
              setShow={setShowSaveToast}
              show={showSaveToast}
              message={
                <FormattedMessage id="eaAccreditationForm.saveToastInformation" />
              }
            />
            <SuccessToast
              setShow={setShowSubmittedToast}
              show={showSubmittedToast}
              message={
                <FormattedMessage id="eaAccreditationForm.submitToastInformation" />
              }
            />
            <Formik
              enableReinitialize={true}
              initialValues={formikInitialValues}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={eaAccreditationFormSchema}
              onSubmit={validateAndOpenSubmissionModal}
            >
              {(formikProps) => (
                <Form onSubmit={formikProps.handleSubmit}>
                  {renderStep(formikProps)}
                </Form>
              )}
            </Formik>
          </Col>
          <Col md="2">
            {isIEFP && (
              <div>
                {["DRAFT"].includes(renewal.state) && (
                  <button
                    className="btn btn-outline-primary d-flex align-items-center justify-content-center mb-4 w-100"
                    type="button"
                    onClick={() => handleSave()}
                  >
                    <FaRegSave />
                    <FormattedMessage id="all.save" />
                  </button>
                )}
                {["DRAFT"].includes(renewal.state) && (
                  <button
                    className="btn btn-primary d-flex align-items-center justify-content-center mb-4 w-100"
                    type="button"
                    onClick={validateAndOpenSubmissionModal}
                  >
                    <FaRegCheckCircle />
                    <FormattedMessage id="all.submitButtonText" />
                  </button>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </SubTemplate>
  );
}
